import { useCallback, useEffect, useRef } from 'react';

interface UseVisibilityTrackingOptions {
    /** Fraction of the element that must be visible (0-1). Defaults to 0.5 (50%). */
    threshold?: number;
    /** How long the element must stay visible before firing, in milliseconds. Defaults to 1000 (1s). */
    dwellTime?: number;
    /** Fire the callback only once per mount. Defaults to true. */
    once?: boolean;
}

/**
 * Tracks when an element has been at least `threshold` visible in the viewport for an
 * uninterrupted `dwellTime`, then calls `onVisible`.
 *
 * Returns a callback ref that must be attached to the element to track. Using a callback
 * ref (instead of a RefObject) means the observer is wired up exactly when the element
 * mounts — important here because the tracked element only renders after data loads.
 *
 * Uses IntersectionObserver to detect visibility and a timer to enforce the dwell time.
 * If the element drops below the threshold before the dwell time elapses, the pending
 * timer is cancelled.
 */
export const useVisibilityTracking = <T extends Element>(
    onVisible: () => void,
    { threshold = 0.5, dwellTime = 1000, once = true }: UseVisibilityTrackingOptions = {},
): ((element: T | null) => void) => {
    const hasFiredRef = useRef(false);
    const onVisibleRef = useRef(onVisible);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Keep the latest callback without needing to re-create the observer.
    useEffect(() => {
        onVisibleRef.current = onVisible;
    }, [onVisible]);

    const clearDwellTimer = useCallback(() => {
        if (dwellTimerRef.current !== undefined) {
            clearTimeout(dwellTimerRef.current);
            dwellTimerRef.current = undefined;
        }
    }, []);

    return useCallback(
        (element: T | null) => {
            // Detach from any previously observed element.
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            clearDwellTimer();

            if (!element || typeof IntersectionObserver === 'undefined') {
                return;
            }

            const observer = new IntersectionObserver(
                ([entry]) => {
                    // Visible enough when at least `threshold` (e.g. 50%) of the element
                    // itself is within the viewport. `intersectionRatio` is the fraction of
                    // the element that is visible, which is exactly what we want to measure.
                    const isVisibleEnough = entry.isIntersecting && entry.intersectionRatio >= threshold;

                    if (isVisibleEnough) {
                        if (dwellTimerRef.current !== undefined || (once && hasFiredRef.current)) {
                            return;
                        }
                        dwellTimerRef.current = setTimeout(() => {
                            dwellTimerRef.current = undefined;
                            if (once && hasFiredRef.current) {
                                return;
                            }
                            hasFiredRef.current = true;
                            onVisibleRef.current();
                            if (once) {
                                observer.disconnect();
                                observerRef.current = null;
                            }
                        }, dwellTime);
                    } else {
                        clearDwellTimer();
                    }
                },
                // Multiple thresholds so the callback fires as the element scrolls through
                // intermediate ratios, not only when it crosses a single point.
                { threshold: [0, 0.1, 0.25, threshold, 0.75, 1] },
            );

            observer.observe(element);
            observerRef.current = observer;
        },
        [threshold, dwellTime, once, clearDwellTimer],
    );
};
